import { type PaymentProfile } from 'dashboard-experience/shared/services'
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'

import { useDeusExMachina } from '~/domains/platform/infra/deus-ex-machina/hocs/with-deus-ex-machina'
import { Bucket } from '~/domains/platform/lib/bucket'
import { SELECTED_PAYMENT_PROFILES_BUCKET_KEY as BUCKET_KEY } from '~/domains/platform/lib/payment-profile-storage'

import { useGetPaymentProfiles } from './use-get-payment-profiles'

export const MAX_SELECTED_PAYMENT_PROFILES = 50

const EMPTY: string[] = []
const EMPTY_PROFILES: PaymentProfile[] = []

const listeners = new Set<() => void>()

// useSyncExternalStore compares snapshots by reference, so the parsed array must stay stable
// until the stored value actually changes.
let snapshot: { serialized: string; value: string[] } | null = null

function emit() {
  listeners.forEach(listener => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): string[] {
  if (typeof window === 'undefined') return EMPTY
  const value = Bucket.local.get(BUCKET_KEY) ?? EMPTY
  const serialized = JSON.stringify(value)
  if (snapshot && snapshot.serialized === serialized) return snapshot.value
  snapshot = { serialized, value }
  return value
}

/**
 * Seleção global de lojas para filtros multi (`PaymentProfileFilter` mode="multi").
 * Store único compartilhado por todos os filtros multi. Para a loja ativa (seleção única
 * global usada pelo gate/drawer) use `useActivePaymentProfile`.
 */
export function useSelectedPaymentProfiles() {
  const selectedProfileIds = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY)

  const { deusExMachina } = useDeusExMachina()
  const stoneAccount = deusExMachina?.entity?.stoneAccount
  const stoneAccountId = stoneAccount?.id ?? ''
  // Fora do one_stone não existe multiloja: semear a seleção faria os services mandarem
  // X-Payment-Profile-Id num cenário em que o BFF resolve a loja sozinho.
  const isOneStone = stoneAccount?.situation === 'one_stone'
  const { data, isFetched } = useGetPaymentProfiles({ stoneAccountId, enabled: isOneStone })
  const paymentProfiles = data ?? EMPTY_PROFILES

  const defaultProfileIds = useMemo(
    () =>
      paymentProfiles
        .filter(profile => profile.enabled)
        .slice(0, MAX_SELECTED_PAYMENT_PROFILES)
        .map(profile => profile.id),
    [paymentProfiles]
  )

  const selectedProfiles = useMemo(
    () => paymentProfiles.filter(profile => selectedProfileIds.includes(profile.id)),
    [paymentProfiles, selectedProfileIds]
  )

  const setSelectedProfileIds = useCallback((ids: string[]) => {
    Bucket.local.set(BUCKET_KEY, ids)
    emit()
  }, [])

  const clear = useCallback(() => {
    Bucket.local.remove(BUCKET_KEY)
    emit()
  }, [])

  const hasSelection = selectedProfileIds.length > 0
  const canSeed = isFetched && defaultProfileIds.length > 0

  // O `PaymentProfileFilter` não monta quando a conta tem uma única loja (ou quando a página
  // esconde o filtro), então o default não pode nascer lá: sem seleção em cache os services
  // mandam o sentinel e a requisição sai sem X-Payment-Profile-Id nenhum.
  useEffect(() => {
    if (!isOneStone || hasSelection || !canSeed) return
    setSelectedProfileIds(defaultProfileIds)
  }, [isOneStone, hasSelection, canSeed, defaultProfileIds, setSelectedProfileIds])

  // Quem busca por loja precisa esperar a seed: disparar antes manda a requisição sem
  // X-Payment-Profile-Id e obriga um segundo fetch quando a seleção chega.
  const isSelectionReady = !isOneStone || hasSelection || (isFetched && defaultProfileIds.length === 0)

  const isDefaultSelection = !hasSelection || defaultProfileIds.every(id => selectedProfileIds.includes(id))

  return {
    selectedProfiles,
    selectedProfileIds,
    defaultProfileIds,
    isDefaultSelection,
    isSelectionReady,
    setSelectedProfileIds,
    clear
  }
}
